import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Zap, Users, Building2, BookOpen, Star, Crown, Award, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PLANS = [
  { id: 'explorateur', nameKey: 'pricing.freeName', free: true, icon: BookOpen, color: '#94a3b8', descKey: 'pricing.explorateurDesc', featureKeys: ['pricing.explorateurF1', 'pricing.explorateurF2', 'pricing.explorateurF3', 'pricing.explorateurF4'], disabled: true },
  { id: 'apprenant', nameKey: 'pricing.apprenantName', price: '6,90€', icon: Zap, color: '#20c997', descKey: 'pricing.apprenantDesc', featureKeys: ['pricing.apprenantF1', 'pricing.apprenantF2', 'pricing.apprenantF3', 'pricing.apprenantF4'], popular: true },
  { id: 'createur_pro', nameKey: 'pricing.createurProName', price: '24,90€', icon: Star, color: '#ffd93d', descKey: 'pricing.createurProDesc', featureKeys: ['pricing.createurProF1', 'pricing.createurProF2', 'pricing.createurProF3', 'pricing.createurProF4'] },
  { id: 'organisation', nameKey: 'pricing.orgName', price: '69€', icon: Building2, color: '#ff6b6b', descKey: 'pricing.orgDesc', featureKeys: ['pricing.orgF1', 'pricing.orgF2', 'pricing.orgF3', 'pricing.orgF4'] },
  { id: 'rh_gpec', nameKey: 'pricing.rhName', price: '149€', icon: Crown, color: '#a78bfa', descKey: 'pricing.rhDesc', featureKeys: ['pricing.rhF1', 'pricing.rhF2', 'pricing.rhF3', 'pricing.rhF4'] },
];

export default function Pricing() {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) pollPaymentStatus(sessionId);
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 6) return;
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
      if (res.data.payment_status === 'paid') {
        setSuccess(t('pricing.successActivated'));
        toast.success(t('pricing.toastActivated'));
        await refreshUser();
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (_) {}
  };

  const handleSubscribe = async (planId) => {
    if (!user) { navigate('/auth?mode=register'); return; }
    if (user.plan === planId) return;
    setError('');
    setLoading(planId);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/payments/checkout`, { plan: planId, origin_url: origin }, { withCredentials: true });
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || t('pricing.genericError'));
      setLoading(null);
    }
  };

  const Wrapper = user
    ? Layout
    : ({ children }) => (
      <div className="min-h-screen bg-[#0f0e17] flex flex-col">
        <nav className="px-4 py-4 flex items-center justify-between border-b border-[#2a2935]">
          <button onClick={() => navigate('/')} className="font-heading font-bold text-xl text-white">Kurso</button>
          <button onClick={() => navigate('/auth')} className="text-sm text-[#94a3b8] hover:text-white transition-colors border border-[#2a2935] px-3 py-1.5 rounded-lg">
            {t('pricing.loginBtn')}
          </button>
        </nav>
        <div className="flex-1">{children}</div>
      </div>
    );

  return (
    <Wrapper>
      <div className="max-w-6xl mx-auto px-4 pt-10 pb-16 space-y-10">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-black text-white mb-3">{t('pricing.pageTitle')}</h1>
          <p className="text-[#94a3b8] text-base max-w-xl mx-auto">
            {t('pricing.pageSubtitle')}
          </p>
        </div>

        {error && (
          <div className="bg-[#ff6b6b]/10 border border-[#ff6b6b]/20 text-[#ff6b6b] rounded-2xl px-4 py-3 text-sm text-center" data-testid="pricing-error">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-[#20c997]/10 border border-[#20c997]/20 text-[#20c997] rounded-2xl px-4 py-3 text-sm text-center" data-testid="pricing-success">
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {PLANS.map((plan, i) => {
            const isCurrent = user?.plan === plan.id || (!user && plan.id === 'explorateur');
            const isLoading = loading === plan.id;
            const Icon = plan.icon;

            return (
              <div
                key={plan.id}
                className={`relative bg-[#16161e] border rounded-2xl p-5 flex flex-col gap-4 transition-all ${
                  plan.popular ? 'border-[#20c997]/40' : 'border-[#2a2935]'
                }`}
                style={{ animationDelay: `${i * 0.05}s` }}
                data-testid={`plan-${plan.id}`}
              >
                {plan.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#20c997] text-[#0f0e17] text-[10px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wide">
                    {t('pricing.popular')}
                  </span>
                )}

                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${plan.color}20` }}>
                      <Icon size={15} style={{ color: plan.color }} />
                    </div>
                    <span className="font-heading font-bold text-white text-sm">{t(plan.nameKey)}</span>
                  </div>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="font-heading text-2xl font-black" style={{ color: plan.color }}>{plan.free ? t('pricing.freePrice') : plan.price}</span>
                    {!plan.free && <span className="text-[#94a3b8] text-xs">{t('pricing.perMonthShort')}</span>}
                  </div>
                  <p className="text-xs text-[#94a3b8] leading-relaxed">{t(plan.descKey)}</p>
                </div>

                <ul className="space-y-1.5 flex-1">
                  {plan.featureKeys.map((featKey) => (
                    <li key={featKey} className="flex items-start gap-2 text-xs text-[#e8e8e8]" data-testid={`${plan.id}-feature`}>
                      <Check size={11} style={{ color: plan.color }} className="mt-0.5 shrink-0" />
                      {t(featKey)}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => !plan.disabled && !isCurrent && handleSubscribe(plan.id)}
                  disabled={isCurrent || isLoading || plan.disabled}
                  className="w-full py-2.5 text-sm rounded-xl font-semibold flex items-center justify-center gap-2 transition-all"
                  style={!isCurrent && !plan.disabled
                    ? { backgroundColor: plan.color, color: '#0f0e17' }
                    : { backgroundColor: '#2a2935', color: '#64748b', cursor: 'default' }}
                  data-testid={`subscribe-${plan.id}-btn`}
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-[#0f0e17] border-t-transparent rounded-full animate-spin" />
                  ) : isCurrent ? (
                    t('pricing.currentPlanBtn')
                  ) : plan.disabled ? (
                    t('pricing.freePrice')
                  ) : (
                    <>{t('pricing.chooseBtn')} <ArrowRight size={13} /></>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* CTA packs */}
        <div className="bg-gradient-to-r from-[#16161e] to-[#1a1927] border border-[#2a2935] rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="font-heading font-bold text-white mb-1">{t('pricing.creditsCtaTitle')}</h3>
            <p className="text-[#94a3b8] text-sm">{t('pricing.creditsCtaDesc')}</p>
          </div>
          <button
            onClick={() => navigate('/credits')}
            className="shrink-0 px-5 py-2.5 rounded-xl bg-[#ffd93d] text-[#0f0e17] font-semibold text-sm flex items-center gap-2 hover:opacity-90 transition-opacity"
            data-testid="go-to-credits-btn"
          >
            <Zap size={14} /> {t('pricing.creditsCtaBtn')}
          </button>
        </div>

        <p className="text-center text-xs text-[#64748b]">
          {t('pricing.footnote')}
        </p>
      </div>
    </Wrapper>
  );
}
